/* ============================================================
   LOVELY JEWELS - Shopify OS 2.0 Theme
   Main JavaScript
   ============================================================ */

/* ------------------------------------------------------------
   1. Shopify Cart API Wrapper
   ------------------------------------------------------------ */

const CartAPI = {
  /**
   * Fetch the current cart state
   */
  async get() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  },

  /**
   * Add item(s) to cart
   * @param {number} variantId - The variant ID
   * @param {number} quantity - Quantity to add
   * @param {object} [properties] - Optional line item properties
   */
  async add(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity, properties }]
      })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.description || 'Could not add to cart');
    }
    return res.json();
  },

  /**
   * Update a line item quantity
   * @param {string} key - The line item key
   * @param {number} quantity - New quantity (0 to remove)
   */
  async update(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.description || 'Could not update cart');
    }
    return res.json();
  },

  /**
   * Clear the entire cart
   */
  async clear() {
    const res = await fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) throw new Error('Could not clear cart');
    return res.json();
  }
};

/* ------------------------------------------------------------
   2. Cart Drawer Controller
   ------------------------------------------------------------ */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    this.body = document.getElementById('cart-drawer-body');
    this.itemCount = document.querySelectorAll('[data-cart-count]');
    this.totalPrice = document.getElementById('cart-total-price');
    this.itemsDescription = document.getElementById('cart-items-description');
    this.emptyState = document.getElementById('cart-empty');
    this.filledState = document.getElementById('cart-filled');
    this.checkoutBtn = document.getElementById('cart-checkout-btn');

    this.bindEvents();
  }

  bindEvents() {
    // Open cart
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close cart
    document.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    // Listen for cart update events
    document.addEventListener('cart:updated', (e) => {
      this.render(e.detail.cart);
    });
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains('is-open');
  }

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const cart = await CartAPI.get();
      this.render(cart);
    } catch (err) {
      console.error('Failed to refresh cart:', err);
    }
  }

  render(cart) {
    // Update count badges
    this.itemCount.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });

    // Toggle empty/filled state
    if (this.emptyState && this.filledState) {
      if (cart.item_count === 0) {
        this.emptyState.style.display = '';
        this.filledState.style.display = 'none';
      } else {
        this.emptyState.style.display = 'none';
        this.filledState.style.display = 'flex';
      }
    }

    // Update total
    if (this.totalPrice) {
      this.totalPrice.textContent = this.formatMoney(cart.total_price);
    }

    // Update items description
    if (this.itemsDescription) {
      this.itemsDescription.textContent = cart.item_count === 0
        ? window.theme.strings.cart_empty
        : cart.item_count + ' article(s) dans votre panier';
    }

    // Render line items
    if (this.body) {
      if (cart.item_count === 0) {
        this.body.innerHTML = '';
      } else {
        this.body.innerHTML = cart.items.map(item => this.renderItem(item)).join('');
        this.bindItemEvents();
      }
    }

    // Checkout button
    if (this.checkoutBtn) {
      if (cart.item_count === 0) {
        this.checkoutBtn.disabled = true;
        this.checkoutBtn.setAttribute('disabled', '');
      } else {
        this.checkoutBtn.disabled = false;
        this.checkoutBtn.removeAttribute('disabled');
      }
      this.checkoutBtn.style.pointerEvents = '';
      this.checkoutBtn.style.opacity = '';
    }
  }

  renderItem(item) {
    const image = item.image
      ? `<img src="${this.resizeImage(item.image, '160x160')}" alt="${this.escapeHtml(item.title)}" loading="lazy">`
      : '';

    const options = item.variant_title || '';

    return `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">${image}</div>
        <div class="cart-item__details">
          <div class="cart-item__title">${this.escapeHtml(item.product_title)}</div>
          ${options ? `<div class="cart-item__options">${this.escapeHtml(options)}</div>` : ''}
          <div class="cart-item__price">${this.formatMoney(item.final_line_price)}</div>
          <div class="cart-item__actions">
            <div class="qty-selector">
              <button class="qty-selector__btn" data-qty-change="-1" data-key="${item.key}" aria-label="Diminuer">
                <svg class="icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
              </button>
              <span class="qty-selector__value">${item.quantity}</span>
              <button class="qty-selector__btn" data-qty-change="1" data-key="${item.key}" aria-label="Augmenter">
                <svg class="icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
              </button>
            </div>
            <button class="cart-item__remove" data-remove-key="${item.key}" aria-label="Supprimer">
              <svg class="icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  bindItemEvents() {
    // Quantity changes
    this.body.querySelectorAll('[data-qty-change]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const change = parseInt(btn.dataset.qtyChange, 10);
        const selector = btn.closest('.qty-selector');
        if (!selector) return;
        const valueEl = selector.querySelector('.qty-selector__value');
        const currentQty = parseInt(valueEl.textContent, 10);
        const newQty = Math.max(0, currentQty + change);

        try {
          const cart = await CartAPI.update(key, newQty);
          this.render(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        } catch (err) {
          console.error('Failed to update quantity:', err);
        }
      });
    });

    // Remove buttons
    this.body.querySelectorAll('[data-remove-key]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.removeKey;
        try {
          const cart = await CartAPI.update(key, 0);
          this.render(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        } catch (err) {
          console.error('Failed to remove item:', err);
        }
      });
    });
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const showCode = window.theme && window.theme.showCurrencyCode;
    return showCode ? `CHF ${amount}` : `${amount} CHF`;
  }

  resizeImage(url, size) {
    if (!url) return '';
    return url.replace(/(\.[^.]+)$/, `_${size}$1`);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

/* ------------------------------------------------------------
   3. Add to Cart Handler
   ------------------------------------------------------------ */

async function handleAddToCart(variantId, quantity = 1) {
  try {
    await CartAPI.add(variantId, quantity);
    const cart = await CartAPI.get();

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

    // Open cart drawer
    if (window.cartDrawer) {
      window.cartDrawer.render(cart);
      window.cartDrawer.open();
    }

    // Show toast notification
    showToast(window.theme?.strings?.added_to_cart || 'Ajouté au panier');
  } catch (err) {
    showToast(err.message, 'error');
  }
}

/* ------------------------------------------------------------
   4. Toast Notifications
   ------------------------------------------------------------ */

function showToast(message, type = 'success') {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);

  requestAnimationFrame(() => toast.classList.add('is-visible'));

  setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

/* Toast styles are injected inline to keep things self-contained */
(function() {
  const style = document.createElement('style');
  style.textContent = `
    .toast {
      position: fixed;
      bottom: 2rem;
      left: 50%;
      transform: translateX(-50%) translateY(1rem);
      padding: 0.75rem 1.5rem;
      border-radius: 0.75rem;
      font-size: 0.875rem;
      font-weight: 500;
      z-index: 100;
      opacity: 0;
      transition: all 0.3s ease;
      pointer-events: none;
    }
    .toast.is-visible {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    .toast--success {
      background: hsl(var(--color-primary));
      color: hsl(var(--color-primary-foreground));
    }
    .toast--error {
      background: hsl(var(--color-destructive));
      color: hsl(var(--color-destructive-foreground));
    }
  `;
  document.head.appendChild(style);
})();

/* ------------------------------------------------------------
   5. Quick View Modal
   ------------------------------------------------------------ */

class QuickViewModal {
  constructor() {
    this.overlay = document.getElementById('quick-view-overlay');
    this.container = document.getElementById('quick-view-modal');
    this.selectedVariantIdx = 0;
    this.quantity = 1;
    this.productData = null;

    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  open(productDataEl) {
    if (!this.overlay || !this.container) return;

    // Parse product data from the element's data attribute
    this.productData = JSON.parse(productDataEl.dataset.product);
    this.selectedVariantIdx = 0;
    this.quantity = 1;

    this.render();
    this.overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-active');
    document.body.style.overflow = '';
    this.productData = null;
  }

  render() {
    if (!this.productData || !this.container) return;

    const product = this.productData;
    const variant = product.variants[this.selectedVariantIdx];
    const price = parseFloat(variant.price);
    const total = (price * this.quantity).toFixed(2);
    const imageUrl = product.images[0] || '';
    const currency = variant.currency_code || 'CHF';

    let variantsHtml = '';
    if (product.variants.length > 1) {
      variantsHtml = `
        <div class="quick-view__variants">
          <span class="quick-view__label">${window.theme?.strings?.variant || 'Variante'}</span>
          <div class="quick-view__variant-btns">
            ${product.variants.map((v, idx) => `
              <button class="btn ${idx === this.selectedVariantIdx ? 'btn--primary btn--sm' : 'btn--outline btn--sm'}"
                      data-variant-idx="${idx}"
                      ${!v.available ? 'disabled' : ''}>
                ${this.escapeHtml(v.title)}
              </button>
            `).join('')}
          </div>
        </div>
      `;
    }

    this.container.innerHTML = `
      <button class="modal__close" data-quick-view-close aria-label="Fermer">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      </button>
      <div class="quick-view__grid">
        <div class="quick-view__image">
          ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
        </div>
        <div class="quick-view__details">
          <h3 class="quick-view__title">${this.escapeHtml(product.title)}</h3>
          <div class="quick-view__price">${currency} ${price.toFixed(2)}</div>
          ${variantsHtml}
          <div class="quick-view__quantity">
            <span class="quick-view__label">${window.theme?.strings?.quantity || 'Quantité'}</span>
            <div class="qty-selector">
              <button class="qty-selector__btn" data-qv-qty="-1" aria-label="Diminuer">
                <svg class="icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
              </button>
              <span class="qty-selector__value" id="qv-qty">${this.quantity}</span>
              <button class="qty-selector__btn" data-qv-qty="1" aria-label="Augmenter">
                <svg class="icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
              </button>
            </div>
          </div>
          <div class="quick-view__total">
            <span>Total :</span>
            <strong>${currency} ${total}</strong>
          </div>
          <button class="btn btn--primary btn--full" id="qv-add-to-cart">
            ${window.theme?.strings?.add_to_cart || 'Ajouter au panier'}
          </button>
          <a href="${product.url}" class="quick-view__link">
            ${window.theme?.strings?.view_product || 'Voir la fiche produit complète'}
          </a>
        </div>
      </div>
    `;

    this.bindModalEvents();
  }

  bindModalEvents() {
    // Close button
    this.container.querySelector('[data-quick-view-close]')?.addEventListener('click', () => this.close());

    // Variant selection
    this.container.querySelectorAll('[data-variant-idx]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.selectedVariantIdx = parseInt(btn.dataset.variantIdx, 10);
        this.render();
      });
    });

    // Quantity
    this.container.querySelectorAll('[data-qv-qty]').forEach(btn => {
      btn.addEventListener('click', () => {
        const change = parseInt(btn.dataset.qvQty, 10);
        this.quantity = Math.max(1, Math.min(10, this.quantity + change));
        this.render();
      });
    });

    // Add to cart
    this.container.querySelector('#qv-add-to-cart')?.addEventListener('click', () => {
      const variant = this.productData.variants[this.selectedVariantIdx];
      handleAddToCart(variant.id, this.quantity);
      this.close();
    });
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

/* ------------------------------------------------------------
   6. Accordion
   ------------------------------------------------------------ */

function initAccordions() {
  document.querySelectorAll('.accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion__item');
      const isOpen = item.classList.contains('is-open');

      // Close all in the same accordion
      const accordion = item.closest('.accordion');
      if (accordion) {
        accordion.querySelectorAll('.accordion__item.is-open').forEach(openItem => {
          openItem.classList.remove('is-open');
        });
      }

      // Toggle current
      if (!isOpen) {
        item.classList.add('is-open');
      }
    });
  });
}

/* ------------------------------------------------------------
   7. Mobile Menu
   ------------------------------------------------------------ */

function initMobileMenu() {
  const toggle = document.getElementById('mobile-menu-toggle');
  const menu = document.getElementById('mobile-menu');
  const iconMenu = document.getElementById('icon-menu');
  const iconClose = document.getElementById('icon-close');

  if (!toggle || !menu) return;

  toggle.addEventListener('click', () => {
    const isOpen = menu.classList.contains('is-open');
    menu.classList.toggle('is-open');

    if (iconMenu && iconClose) {
      iconMenu.style.display = isOpen ? '' : 'none';
      iconClose.style.display = isOpen ? 'none' : '';
    }
  });

  // Close on link click
  menu.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      menu.classList.remove('is-open');
      if (iconMenu && iconClose) {
        iconMenu.style.display = '';
        iconClose.style.display = 'none';
      }
    });
  });
}

/* ------------------------------------------------------------
   8. Product Image Gallery
   ------------------------------------------------------------ */

function initProductGallery() {
  const mainImage = document.getElementById('product-main-image');
  if (!mainImage) return;

  document.querySelectorAll('[data-thumbnail]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      mainImage.src = thumb.dataset.thumbnail;
      mainImage.alt = thumb.alt || '';

      // Update active state
      document.querySelectorAll('[data-thumbnail]').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

/* ------------------------------------------------------------
   9. Variant Selector (Product Page)
   ------------------------------------------------------------ */

function initVariantSelector() {
  const form = document.querySelector('[data-product-form]');
  if (!form) return;

  const priceEl = document.getElementById('product-price');
  const addBtn = document.getElementById('product-add-btn');
  const variantInput = form.querySelector('[name="id"]');

  form.querySelectorAll('[data-variant-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      const variantId = btn.dataset.variantBtn;
      const price = btn.dataset.price;
      const available = btn.dataset.available === 'true';

      // Update hidden input
      if (variantInput) variantInput.value = variantId;

      // Update price
      if (priceEl && price) {
        priceEl.textContent = `CHF ${parseFloat(price).toFixed(2)}`;
      }

      // Update button state
      if (addBtn) {
        addBtn.disabled = !available;
        addBtn.textContent = available
          ? (window.theme?.strings?.add_to_cart || 'Ajouter au panier')
          : (window.theme?.strings?.sold_out || 'Épuisé');
      }

      // Update active variant button
      form.querySelectorAll('[data-variant-btn]').forEach(b => {
        b.classList.remove('btn--primary');
        b.classList.add('btn--outline');
      });
      btn.classList.remove('btn--outline');
      btn.classList.add('btn--primary');

      // Update product image to variant image
      const variantImage = btn.dataset.image;
      if (variantImage) {
        const mainImg = document.getElementById('product-main-image');
        if (mainImg) mainImg.src = variantImage;

        // Update active thumbnail
        document.querySelectorAll('.product-gallery__thumb').forEach(thumb => {
          thumb.classList.remove('is-active');
          if (thumb.dataset.thumbnail === variantImage) {
            thumb.classList.add('is-active');
          }
        });
      }
    });
  });
}

/* ------------------------------------------------------------
   10. Initialization
   ------------------------------------------------------------ */

document.addEventListener('DOMContentLoaded', () => {
  // Global theme config
  window.theme = window.theme || {};

  // Initialize all components
  window.cartDrawer = new CartDrawer();
  window.quickView = new QuickViewModal();

  initAccordions();
  initMobileMenu();
  initProductGallery();
  initVariantSelector();

  // Initial cart count on page load
  CartAPI.get().then(cart => {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });
  });

  // Product form AJAX submit
  document.querySelectorAll('[data-product-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantId = form.querySelector('[name="id"]')?.value;
      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
      if (variantId) {
        await handleAddToCart(variantId, quantity);
      }
    });
  });

  // Quick view triggers
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (trigger) {
      e.preventDefault();
      window.quickView.open(trigger);
    }
  });

  // Lazy image fade-in
  initLazyImages();

  // Scroll-to-top on page load for product pages
  if (document.querySelector('.product-page')) {
    window.scrollTo(0, 0);
  }
});

/* ------------------------------------------------------------
   11. Lazy Image Loading (fade-in effect)
   ------------------------------------------------------------ */

function initLazyImages() {
  // Mark already-loaded images
  document.querySelectorAll('img[loading="lazy"]').forEach(img => {
    if (img.complete) {
      img.classList.add('loaded');
    } else {
      img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
    }
  });

  // Observe dynamically added images
  if ('MutationObserver' in window) {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          const imgs = node.matches?.('img[loading="lazy"]')
            ? [node]
            : (node.querySelectorAll?.('img[loading="lazy"]') || []);
          imgs.forEach(img => {
            if (img.complete) {
              img.classList.add('loaded');
            } else {
              img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
            }
          });
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
